import React from "react";
import Chart from "react-apexcharts";
import PropTypes from "prop-types"; // Pour la validation des props

export default function ChartFullBg({
  data = [65, 59, 80, 81, 56, 55, 40],
  dataLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
  colors = ["#f97316"],
  title = "Ventes", // Prop pour le titre
  tooltipSuffix = "  FCFA", // Prop pour le suffixe de l'infobulle
  period = "week", // Nouvelle prop pour la période (week, month, year)
}) {
  // Validation et conversion des données pour s'assurer qu'elles sont des nombres
  const processedData = data.map((item) => {
    const num = Number(item);
    return isNaN(num) ? 0 : num;
  });

  // Si les données sont vides ou tous les points sont à 0, afficher un message
  if (!processedData.length || processedData.every((val) => val === 0)) {
    return (
      <div className="flex items-center justify-center h-full w-full text-gray-500">
        <p>Aucune donnée disponible pour {title}</p>
      </div>
    );
  }

  const options = {
    chart: {
      type: "area",
      height: "100%",
      width: "100%",
      fontFamily: "Inter, sans-serif",
      dropShadow: {
        enabled: true,
      },
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 6, // Même épaisseur que les autres graphiques
    },
    markers: {
      size: processedData.length === 1 ? 8 : 4, // Points plus gros pour un seul élément
      colors: colors,
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: {
        size: processedData.length === 1 ? 10 : 6,
      },
    },
    xaxis: {
      show: true,
      categories: dataLabels,
      labels: {
        show: true,
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500",
        },
        // Rotation des étiquettes en fonction de la période
        rotate: period === "month" ? -45 : 0,
        // Adaptation du format des dates pour les périodes longues
        rotateAlways: period === "month",
        // Limiter le nombre d'étiquettes affichées si nombreuses
        maxHeight: 120,
        trim: true,
      },
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: true,
      },
    },
    yaxis: {
      title: {
        text: "Chiffre d'affaire",
      },
      show: true,
      labels: {
        show: true,
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500",
        },
        formatter: function (value) {
          // Formater les nombres avec des séparateurs de milliers pour l'axe Y
          return value != null ? value.toLocaleString("fr-FR") : "0";
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        opacityFrom: 0.35,
        opacityTo: 0,
        shade: colors[0],
        gradientToColors: colors,
      },
    },
    tooltip: {
      enabled: true,
      x: {
        show: false,
      },
      y: {
        formatter: function (val) {
          // Formater les nombres avec des séparateurs de milliers
          return `${val.toLocaleString("fr-FR")}${tooltipSuffix}`;
        },
      },
    },
    grid: {
      show: false,
    },
    colors: colors,
  };

  const series = [
    {
      name: title || "Ventes",
      data: processedData, // Utilisation des données traitées
    },
  ];

  return (
    <Chart
      options={options}
      series={series}
      type="area"
      height="100%"
      width="100%"
      className="h-full w-full"
    />
  );
}

// Validation des props
ChartFullBg.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number), // data doit être un tableau de nombres
  dataLabels: PropTypes.arrayOf(PropTypes.string), // dataLabels doit être un tableau de chaînes
  colors: PropTypes.arrayOf(PropTypes.string), // colors doit être un tableau de chaînes
  title: PropTypes.string, // title doit être une chaîne
  tooltipSuffix: PropTypes.string, // tooltipSuffix doit être une chaîne
  period: PropTypes.oneOf(["week", "month", "year"]), // period doit être 'week', 'month', ou 'year'
};

// Valeurs par défaut des props
ChartFullBg.defaultProps = {
  data: [65, 59, 80, 81, 56, 55, 40],
  dataLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
  colors: ["#f97316"],
  title: "Ventes",
  tooltipSuffix: " ventes",
  period: "week",
};
